'use strict';

const RULEBOOK_TABS = Object.freeze([
    Object.freeze({key: 'principles', label: '평가원칙'}),
    Object.freeze({key: 'feedback', label: '피드백 흐름'}),
    Object.freeze({key: 'versions', label: '기준 버전'}),
    Object.freeze({key: 'cycles', label: '자동 실행 이력'}),
    Object.freeze({key: 'validation', label: '자동 검증'}),
]);

const DISPOSITION_KEYS = Object.freeze(['ADD', 'UPDATE', 'PASS', 'DEFER']);
const EMPTY_LABEL = '—';
const KST_OFFSET_MS = 9 * 60 * 60 * 1000;
const MAX_SERVER_MESSAGE_CHARS = 300;

function isObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function safeCount(value) {
    return typeof value === 'number' && Number.isFinite(value) && value >= 0
        ? Math.floor(value)
        : 0;
}

/** 서버가 값을 주지 않은 것과 실제 0을 구분해야 하는 열(버전별 원칙 수)에 쓴다. */
function optionalCount(value) {
    return typeof value === 'number' && Number.isFinite(value) && value >= 0
        ? Math.floor(value)
        : null;
}

function safeText(value, fallback = '') {
    return typeof value === 'string' ? value : fallback;
}

function safeId(value) {
    return typeof value === 'number' && Number.isFinite(value) ? Math.floor(value) : null;
}

function normalizePrinciple(value) {
    if (!isObject(value)) return null;
    const ruleCode = safeText(value.rule_code).trim();
    const title = safeText(value.title).trim();
    if (!ruleCode || !title) return null;
    return {
        ruleCode,
        topic: safeText(value.topic),
        title,
        appliesWhen: safeText(value.applies_when),
        ruleText: safeText(value.rule_text),
        exceptions: safeText(value.exceptions),
        rationale: safeText(value.rationale),
    };
}

function normalizePrincipleList(value) {
    return Array.isArray(value) ? value.map(normalizePrinciple).filter(Boolean) : [];
}

function normalizeLastRun(value) {
    if (!isObject(value)) return null;
    return {
        status: safeText(value.status),
        decision: value.decision === null ? null : safeText(value.decision),
        createdAt: value.created_at === null ? null : safeText(value.created_at),
    };
}

function normalizeActiveVersion(value) {
    if (!isObject(value)) return null;
    return {
        id: safeId(value.id),
        versionNo: safeId(value.version_no),
        origin: safeText(value.origin),
        activatedAt: value.activated_at === null ? null : safeText(value.activated_at, null),
    };
}

/** overview·versions 응답이 공유하는 head 요약. 스위치와 활성 버전만 남긴다. */
function normalizeHead(value) {
    const head = isObject(value) ? value : {};
    return {
        activeVersionId: safeId(head.active_version_id),
        activeVersion: normalizeActiveVersion(head.active_version),
        source: safeText(head.source, 'seed'),
        pinned: head.pinned === true,
        candidateGenerationPaused: head.candidate_generation_paused === true,
        activationPaused: head.activation_paused === true,
    };
}

function normalizeAutomation(value) {
    const automation = isObject(value) ? value : {};
    return {
        activationMode: safeText(automation.activation_mode, 'review_only'),
        maxCyclesPerDay: safeCount(automation.max_cycles_per_day),
        maxSourcesPerCycle: safeCount(automation.max_sources_per_cycle),
        dailyBudgetDate: safeText(automation.daily_budget_date, null),
        dailyCyclesStarted: safeCount(automation.daily_cycles_started),
        running: automation.running === true,
        // null = 백엔드가 아직 이 계약을 주지 않음 → 화면이 관리자 등급으로 대신 판단한다.
        canRunAutomation: typeof automation.can_run_automation === 'boolean'
            ? automation.can_run_automation
            : null,
        schedule: safeText(automation.schedule),
    };
}

function normalizeRulebookOverview(payload) {
    const data = isObject(payload) ? payload : {};
    const feedback = isObject(data.feedback) ? data.feedback : {};
    const rulebook = isObject(data.rulebook) ? data.rulebook : {};
    const validation = isObject(data.validation) ? data.validation : {};
    const principles = normalizePrincipleList(rulebook.principles);
    const head = normalizeHead(rulebook);
    const declaredCount = optionalCount(rulebook.principle_count);

    return {
        operatingModel: {
            unit: 'company_answer',
            feedbackPerAiEvaluation: 1,
            consensusRequired: false,
            secondReview: 'sample_only',
        },
        feedback: {
            total: safeCount(feedback.total),
            accepted: safeCount(feedback.accepted),
            corrected: safeCount(feedback.corrected),
            rejected: safeCount(feedback.rejected),
            readyForRulebook: safeCount(feedback.ready_for_rulebook),
            waitingForLedger: safeCount(feedback.waiting_for_ledger),
        },
        rulebook: {
            stage: safeText(rulebook.stage, 'FOUNDATION'),
            source: head.source,
            activeVersionId: head.activeVersionId,
            activeVersion: head.activeVersion,
            pinned: head.pinned,
            candidateGenerationPaused: head.candidateGenerationPaused,
            activationPaused: head.activationPaused,
            principleCount: declaredCount === null ? principles.length : declaredCount,
            principles,
            automationActivationEnabled: rulebook.automation_activation_enabled === true,
        },
        automation: normalizeAutomation(data.automation),
        validation: {
            corpus: safeCount(validation.corpus),
            holdout: safeCount(validation.holdout),
            quarantined: safeCount(validation.quarantined),
            runs: safeCount(validation.runs),
            lastRun: normalizeLastRun(validation.last_run),
        },
    };
}

function normalizeVersionSummary(value) {
    if (!isObject(value)) return null;
    const id = safeId(value.id);
    if (id === null) return null;
    return {
        id,
        versionNo: safeId(value.version_no),
        origin: safeText(value.origin),
        status: safeText(value.status),
        isActive: value.is_active === true,
        validationDecision: value.validation_decision === null
            ? null
            : safeText(value.validation_decision, null),
        principleCount: optionalCount(value.principle_count),
        createdAt: value.created_at === null ? null : safeText(value.created_at, null),
    };
}

function normalizeVersionList(payload) {
    const data = isObject(payload) ? payload : {};
    return {
        head: normalizeHead(data.head),
        versions: Array.isArray(data.versions)
            ? data.versions.map(normalizeVersionSummary).filter(Boolean)
            : [],
    };
}

function normalizeVersionDetail(payload) {
    const data = isObject(payload) ? payload : {};
    const principles = normalizePrincipleList(data.principles);
    const declaredCount = optionalCount(data.principle_count);
    return {
        id: safeId(data.id),
        versionNo: safeId(data.version_no),
        origin: safeText(data.origin),
        status: safeText(data.status),
        isActive: data.is_active === true,
        validationDecision: data.validation_decision === null
            ? null
            : safeText(data.validation_decision, null),
        createdAt: data.created_at === null ? null : safeText(data.created_at, null),
        // 백엔드 실제 키는 content. content_md 는 이전 문서 계약과의 호환용이다.
        content: safeText(data.content) || safeText(data.content_md),
        principles,
        principleCount: declaredCount === null ? principles.length : declaredCount,
    };
}

/** safety_findings 는 자유 JSON 이라 어떤 모양이든 표시용 텍스트 한 덩어리로 바꾼다. */
function safetyFindingsText(value) {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (Array.isArray(value)) {
        return value.map(safetyFindingsText).filter((item) => item !== '').join('\n');
    }
    try {
        return JSON.stringify(value);
    } catch (err) {
        return '';
    }
}

function normalizeDispositions(value) {
    const source = isObject(value) ? value : {};
    const result = {};
    for (const key of DISPOSITION_KEYS) result[key] = safeCount(source[key]);
    return result;
}

function normalizeCycleReview(value) {
    if (!isObject(value)) return null;
    return {
        verdict: safeText(value.verdict),
        reason: safeText(value.reason),
    };
}

function normalizeCycleVersion(value) {
    if (!isObject(value)) return null;
    const id = safeId(value.id);
    if (id === null) return null;
    return {
        id,
        versionNo: safeId(value.version_no),
        status: safeText(value.status),
        validationDecision: value.validation_decision === null
            ? null
            : safeText(value.validation_decision, null),
    };
}

function normalizeCycle(value) {
    if (!isObject(value)) return null;
    const id = safeId(value.id);
    if (id === null) return null;
    return {
        id,
        status: safeText(value.status),
        decision: safeText(value.decision),
        errorCode: safeText(value.error_code),
        reasoning: safeText(value.reasoning),
        safetyFindings: safetyFindingsText(value.safety_findings),
        review: normalizeCycleReview(value.review),
        sourceCount: safeCount(value.source_count),
        dispositions: normalizeDispositions(value.dispositions),
        version: normalizeCycleVersion(value.version),
        createdAt: value.created_at === null ? null : safeText(value.created_at, null),
    };
}

function normalizeCycleList(payload) {
    const data = isObject(payload) ? payload : {};
    return {
        cycles: Array.isArray(data.cycles)
            ? data.cycles.map(normalizeCycle).filter(Boolean)
            : [],
    };
}

/** 모르는 코드는 숨기지 않고 원문 그대로 중립 배지로 보여준다. */
function metaLookup(table, value, {upper = false} = {}) {
    const raw = safeText(value).trim();
    if (!raw) return {label: EMPTY_LABEL, variant: 'secondary'};
    const key = upper ? raw.toUpperCase() : raw;
    return table[key] || {label: raw, variant: 'secondary'};
}

const STAGE_META = {
    ACTIVE: {label: '활성 기준', variant: 'success'},
    SEED: {label: '기본 기준', variant: 'info'},
    FOUNDATION: {label: '검증 기반 구축 중', variant: 'light'},
};

const SOURCE_META = {
    db: {label: 'DB 버전', variant: 'success'},
    seed: {label: '파일 시드', variant: 'info'},
    none: {label: '사용 안 함', variant: 'secondary'},
};

const ACTIVATION_MODE_META = {
    review_only: {label: '자동 반영(사후 검토)', variant: 'warning'},
    validated: {label: '자동 검증 후 반영', variant: 'success'},
};

const ORIGIN_META = {
    SEED: {label: '시드', variant: 'info'},
    OPERATOR: {label: '운영자', variant: 'primary'},
    AI: {label: 'AI', variant: 'secondary'},
};

const VERSION_STATUS_META = {
    CANDIDATE: {label: '후보', variant: 'info'},
    ACTIVE: {label: '활성', variant: 'success'},
    SUPERSEDED: {label: '이전 활성', variant: 'secondary'},
    REJECTED: {label: '거부', variant: 'danger'},
    BLOCKED: {label: '차단', variant: 'danger'},
};

const VALIDATION_DECISION_META = {
    REVIEW_ONLY: {label: '사후 검토', variant: 'warning'},
    ACCEPTED: {label: '검증 통과', variant: 'success'},
    REJECTED: {label: '거부', variant: 'danger'},
    SAFETY_REJECTED: {label: '거부', variant: 'danger'},
    STALE: {label: '기준 변경', variant: 'secondary'},
    GOLDEN_CHANGED: {label: '기준 변경', variant: 'secondary'},
    NO_EFFECT: {label: '변경 없음', variant: 'secondary'},
    INSUFFICIENT_DATA: {label: '자료 부족', variant: 'secondary'},
    VALIDATION_RUN_ERROR: {label: '오류', variant: 'danger'},
    INTERNAL_ERROR: {label: '오류', variant: 'danger'},
};

const CYCLE_STATUS_META = {
    CANDIDATE: {label: '후보 생성', variant: 'success'},
    NO_CHANGE: {label: '변경 없음', variant: 'secondary'},
    NO_SOURCES: {label: '소스 없음', variant: 'secondary'},
    REJECTED: {label: '거부', variant: 'danger'},
    ERROR: {label: '오류', variant: 'danger'},
};

const DECISION_META = {
    ADD: {label: '추가', variant: 'success'},
    UPDATE: {label: '갱신', variant: 'primary'},
    PASS: {label: '이미 반영', variant: 'secondary'},
    DEFER: {label: '보류', variant: 'warning'},
};

const REVIEW_VERDICT_META = {
    APPROVE: {label: '승인', variant: 'success'},
    REJECT: {label: '거부', variant: 'danger'},
};

function stageMeta(value) {
    return metaLookup(STAGE_META, value, {upper: true});
}

function sourceMeta(value) {
    const raw = safeText(value).trim().toLowerCase();
    return metaLookup(SOURCE_META, raw);
}

function activationModeMeta(value) {
    const raw = safeText(value).trim().toLowerCase();
    return metaLookup(ACTIVATION_MODE_META, raw);
}

function originMeta(value) {
    return metaLookup(ORIGIN_META, value, {upper: true});
}

function versionStatusMeta(value) {
    return metaLookup(VERSION_STATUS_META, value, {upper: true});
}

function validationDecisionMeta(value) {
    return metaLookup(VALIDATION_DECISION_META, value, {upper: true});
}

function cycleStatusMeta(value) {
    return metaLookup(CYCLE_STATUS_META, value, {upper: true});
}

function decisionMeta(value) {
    return metaLookup(DECISION_META, value, {upper: true});
}

function reviewVerdictMeta(value) {
    return metaLookup(REVIEW_VERDICT_META, value, {upper: true});
}

/** 우선순위: 정책 꺼짐 → 사람이 켠 스위치 → 실행 중 → 정상. */
function automationStatusMeta(overview) {
    const rulebook = isObject(overview) && isObject(overview.rulebook) ? overview.rulebook : {};
    const automation = isObject(overview) && isObject(overview.automation) ? overview.automation : {};
    if (!rulebook.automationActivationEnabled) {
        return {label: '자동 반영 꺼짐', variant: 'secondary', description: '설정에서 자동 활성화가 꺼져 있습니다.'};
    }
    if (rulebook.pinned) {
        return {label: '버전 고정', variant: 'warning', description: '현재 활성 버전을 자동화가 바꾸지 못합니다.'};
    }
    if (rulebook.candidateGenerationPaused && rulebook.activationPaused) {
        return {label: '자동 순환 중지', variant: 'danger', description: '후보 생성과 자동 활성화를 모두 멈춘 상태입니다.'};
    }
    if (rulebook.candidateGenerationPaused) {
        return {label: '후보 생성 중지', variant: 'warning', description: '새 판단 기준 후보를 만들지 않습니다.'};
    }
    if (rulebook.activationPaused) {
        return {label: '활성화 중지', variant: 'warning', description: '후보가 만들어져도 자동으로 켜지지 않습니다.'};
    }
    if (automation.running) {
        return {label: '자동 실행 중', variant: 'primary', description: '자동 순환 회차가 지금 실행되고 있습니다.'};
    }
    return {
        label: '자동 반영 운영 중',
        variant: 'success',
        description: '매일 회차가 돌고 검토를 통과한 후보가 자동 반영됩니다.',
    };
}

function pad2(value) {
    return String(value).padStart(2, '0');
}

/** 브라우저 시간대와 무관하게 KST 기준 오늘 날짜(YYYY-MM-DD). */
function currentKstDateKey(now) {
    const base = now instanceof Date && !Number.isNaN(now.getTime()) ? now : new Date();
    const kst = new Date(base.getTime() + KST_OFFSET_MS);
    return `${kst.getUTCFullYear()}-${pad2(kst.getUTCMonth() + 1)}-${pad2(kst.getUTCDate())}`;
}

function dailyCyclesStartedToday(automation, now) {
    if (!isObject(automation)) return 0;
    const budgetDate = safeText(automation.dailyBudgetDate);
    if (!budgetDate || budgetDate !== currentKstDateKey(now)) return 0;
    return safeCount(automation.dailyCyclesStarted);
}

/**
 * offset/Z 가 있으면 KST 로 변환하고, offset 없는 서버 timestamp 는 이미 KST 로 본다.
 * 파싱할 수 없으면 화면에 '—' 를 보여준다.
 */
function formatKstDateTime(value) {
    const raw = safeText(value).trim();
    if (!raw) return EMPTY_LABEL;
    const naive = raw.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
    if (naive && !/(?:Z|[+-]\d{2}:?\d{2})$/.test(raw)) {
        return `${naive[1]}-${naive[2]}-${naive[3]} ${naive[4]}:${naive[5]}`;
    }
    const parsed = Date.parse(raw);
    if (Number.isNaN(parsed)) return EMPTY_LABEL;
    const kst = new Date(parsed + KST_OFFSET_MS);
    return `${kst.getUTCFullYear()}-${pad2(kst.getUTCMonth() + 1)}-${pad2(kst.getUTCDate())}`
        + ` ${pad2(kst.getUTCHours())}:${pad2(kst.getUTCMinutes())}`;
}

/** 복귀는 사람이 만든 버전이나 과거 활성본에만 허용된다(AI 후보 활성화는 백엔드가 409). */
function canRollbackVersion(version) {
    if (!isObject(version) || version.isActive === true) return false;
    const status = safeText(version.status).toUpperCase();
    const origin = safeText(version.origin).toUpperCase();
    if (status === 'ACTIVE' || status === 'BLOCKED' || status === 'REJECTED') return false;
    return origin !== 'AI' || status === 'SUPERSEDED';
}

/** 활성 버전 차단은 백엔드가 거부한다 — 먼저 다른 버전으로 복귀해야 한다. */
function canBlockVersion(version) {
    if (!isObject(version) || version.isActive === true) return false;
    return safeText(version.status).toUpperCase() !== 'BLOCKED';
}

/** 사용자에게 보여도 되는 서버 문구만 통과시킨다(HTML 오류 페이지·과도한 길이 차단). */
function safeServerMessage(value) {
    const text = safeText(value).trim();
    if (!text || text.length > MAX_SERVER_MESSAGE_CHARS || text.startsWith('<')) return '';
    return text;
}

/** 판단 기준 오류 본문은 plain text / {message} / {error} / HTML 이 섞여 있다. */
function apiErrorMessage(error, fallback) {
    const data = isObject(error) && isObject(error.response) ? error.response.data : undefined;
    if (typeof data === 'string') {
        return safeServerMessage(data) || fallback;
    }
    if (isObject(data)) {
        return safeServerMessage(data.message) || safeServerMessage(data.error) || fallback;
    }
    return fallback;
}

function validationStatusMeta(validation = {}) {
    const lastDecision = validation?.lastRun?.decision;
    if (lastDecision === 'ACCEPTED') {
        return {
            label: '최근 검증 통과',
            variant: 'success',
            description: '새 후보가 지금 쓰는 판단 기준보다 나쁘지 않아 자동 검증을 통과했습니다.',
        };
    }
    if (lastDecision === 'REJECTED') {
        return {
            label: '최근 후보 거부',
            variant: 'danger',
            description: '새 후보가 지금 쓰는 판단 기준보다 나빠 실제 평가에는 적용되지 않았습니다.',
        };
    }
    if (safeCount(validation?.holdout) === 0) {
        return {
            label: '시험 사례 대기',
            variant: 'warning',
            description: '시험 전용 사례가 쌓이면 새 후보와 지금 쓰는 판단 기준을 자동으로 비교합니다.',
        };
    }
    return {
        label: '검증 준비 완료',
        variant: 'info',
        description: '시험 전용 사례가 준비되어 다음 판단 기준 후보를 비교할 수 있습니다.',
    };
}

module.exports = {
    RULEBOOK_TABS,
    DISPOSITION_KEYS,
    normalizeRulebookOverview,
    normalizeVersionList,
    normalizeVersionDetail,
    normalizeCycleList,
    stageMeta,
    sourceMeta,
    activationModeMeta,
    automationStatusMeta,
    originMeta,
    versionStatusMeta,
    validationDecisionMeta,
    cycleStatusMeta,
    decisionMeta,
    reviewVerdictMeta,
    validationStatusMeta,
    canRollbackVersion,
    canBlockVersion,
    formatKstDateTime,
    currentKstDateKey,
    dailyCyclesStartedToday,
    apiErrorMessage,
};
